import {
  CardContentInner,
  Datagrid,
  Record,
  ResourceContextProvider,
  Show,
  useVersion,
  TextField as RaTextField,
  Identifier,
  useTranslate,
  FunctionField,
  BooleanField,
} from "react-admin";
import { EditActions } from "../../common/form/FormActions";
import {
  Card,
  CardContent,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@material-ui/core";
import {
  DeviceUsageOutput,
  DeviceUsageSummary,
  MasterEldDeviceUsageApiFactory,
  TenantRegistrationDto,
} from "../../../common/data/service-proxies";
import { useAxiosInstance } from "../../../common/hooks/useAxiosInstance";
import { useEffect, useMemo, useReducer, useState } from "react";
import { GenericCompactDateOrTimePicker } from "../../common/form/GenericCompactDateOrTimePicker";
import { DatePicker } from "@material-ui/pickers";
import styles from "./TenantEldDevicesUsage.module.css";
import { CompactListBase } from "../../common/form/CompactListBase";
import resourceNames from "../../../common/data/resourceNames";
import { useTableHeaderStyles } from "../../assets/maintenance-dashboard/common";
import { addDays, addMonths, formatDistance, parseISO } from "date-fns";
import { ArrayField } from "../../common/data-grid-fields/ArrayField";

type Action = "SET_DATEFROM" | "SET_DATETO";

interface Props {}

interface ViewProps {
  record?: Record;
}

interface TenantEldDeviceUsageDetailProps {
  record?: DeviceUsageOutput;
  id?: Identifier;
  resource?: string;
}

interface IFilter {
  dateFrom: Date;
  dateTo: Date;
}

interface IAction {
  type: Action;
  payload: Date;
  tenantId?: string;
}

const reducer = (prev: IFilter, action: IAction) => {
  if (!action) {
    return prev;
  }

  switch (action.type) {
    case "SET_DATEFROM":
      return { ...prev, dateFrom: action.payload };
    case "SET_DATETO":
      return { ...prev, dateTo: action.payload };
  }
  return prev;
};

export const TenantEldDevicesUsage = (props: Props) => {
  return (
    <Show
      {...props}
      title="View ELD devices usage"
      actions={<EditActions />}
      component="div"
    >
      <TenantEldDevicesUsageView />
    </Show>
  );
};

const TenantEldDevicesUsageView = (props: ViewProps) => {
  const translate = useTranslate();
  const version = useVersion();
  const axios = useAxiosInstance();
  const today = new Date();
  const previousMonth = addMonths(today, -1);
  const [filter, dispatch] = useReducer(reducer, {
    dateFrom: new Date(
      previousMonth.getFullYear(),
      previousMonth.getMonth(),
      1
    ),
    dateTo: addDays(new Date(today.getFullYear(), today.getMonth(), 1), -1),
  });

  const [data, setData] = useState<DeviceUsageSummary>();
  const [loading, setLoading] = useState<boolean>(false);

  const record = props.record as TenantRegistrationDto;
  useEffect(() => {
    if (!axios) {
      return;
    }

    if (!filter) {
      return;
    }

    if (!props.record) {
      return;
    }

    const record = props.record as TenantRegistrationDto;
    if (!record.createdTenantId) {
      return;
    }

    setLoading(true);
    MasterEldDeviceUsageApiFactory(undefined, axios.defaults.baseURL, axios)
      .apiAppMasterelddeviceusageElddeviceusageTenantIdGet(
        record.createdTenantId,
        filter.dateFrom.toISOString(),
        filter.dateTo.toISOString()
      )
      .then((response) => {
        setData(response.data);
      })
      .finally(() => {
        setLoading(false);
      });
  }, [axios, filter, props.record, version]);

  const finalFilter = useMemo(
    () => ({ ...filter, tenantId: record.createdTenantId }),
    [filter, record.createdTenantId]
  );

  return (
    <Card>
      <CardContent>
        <CardContentInner>
          <div className={styles.header}>
            <Typography variant="h6">{record.name}</Typography>
            <div className={styles.filters}>
              <GenericCompactDateOrTimePicker
                variant="outlined"
                PickerComponent={DatePicker}
                source="dateFrom"
                label="Date from"
                value={filter.dateFrom}
                onChange={(value) =>
                  dispatch({ type: "SET_DATEFROM", payload: value })
                }
              />
              <GenericCompactDateOrTimePicker
                variant="outlined"
                PickerComponent={DatePicker}
                source="dateTo"
                label="Date to"
                value={filter.dateTo}
                onChange={(value) =>
                  dispatch({ type: "SET_DATETO", payload: value })
                }
              />
            </div>
          </div>
          {loading ? (
            <div>
              <CircularProgress />
            </div>
          ) : (
            <div className={styles.root}>
              <div className={styles.summary}>
                <div>
                  <div>ELD devices used by the customer vehicles</div>
                  <div>{data?.totalDevicesUsedInVehicles ?? "-"}</div>
                </div>
                <div>
                  <div>Total ELD devices used by the customer</div>
                  <div>{data?.totalDevicesUsed ?? "-"}</div>
                </div>
              </div>
              <ResourceContextProvider
                value={resourceNames.MASTER_TENANT_ELDS_USAGE}
              >
                <CompactListBase
                  perPage={5000}
                  sort={{ field: "name", order: "asc" }}
                  basePath="/"
                  resource={resourceNames.MASTER_TENANT_ELDS_USAGE}
                  filter={finalFilter}
                >
                  <Datagrid
                    hasBulkActions={false}
                    optimized
                    expand={<TenantEldDeviceUsageDetail />}
                  >
                    <RaTextField
                      source="serialNumber"
                      label="Serial number"
                      sortable={false}
                    />
                    <BooleanField source="used" label="Used" sortable={false} />
                    <ArrayField
                      source="usedInVehicles"
                      label="Used in vehicle(s)"
                      displayedElementsCount={2}
                    />
                    <FunctionField
                      source="currentStatus"
                      label="Current status"
                      sortable={false}
                      render={(x) => (
                        <Typography variant="body2">
                          {translate(
                            `appData.eldUsageTypes.${x?.currentStatus}`,
                            {
                              _: x?.currentStatus,
                            }
                          )}
                        </Typography>
                      )}
                    />
                  </Datagrid>
                </CompactListBase>
              </ResourceContextProvider>
            </div>
          )}
        </CardContentInner>
      </CardContent>
    </Card>
  );
};

const TenantEldDeviceUsageDetail = ({
  record,
  id,
  resource,
}: TenantEldDeviceUsageDetailProps) => {
  const tableClasses = useTableHeaderStyles();
  const translate = useTranslate();

  return (
    <TableContainer>
      <Table size="small">
        <TableHead>
          <TableRow className={tableClasses.tableHead}>
            <TableCell>Status</TableCell>
            <TableCell>Since</TableCell>
            <TableCell>Until</TableCell>
            <TableCell>Status duration</TableCell>
            <TableCell>Vehicle</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {record &&
            record.usage?.map((x) => (
              <TableRow className={tableClasses.row} key={x.startTime}>
                <TableCell>
                  <Typography variant="body2">
                    {translate(`appData.eldUsageTypes.${x.usageType}`, {
                      _: x.usageType,
                    })}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="body2">
                    {parseISO(x.startTime!).toLocaleString("en-US")}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="body2">
                    {x.endTime
                      ? parseISO(x.endTime).toLocaleString("en-US")
                      : "-"}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="body2">
                    {formatDistance(
                      parseISO(x.endTime!),
                      parseISO(x.startTime!)
                    )}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="body2">
                    {x.vehicleName ?? "-"}
                  </Typography>
                </TableCell>
              </TableRow>
            ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
};
